import { kioskConnect, kioskCurrent } from '@/lib/api';
import { isKioskBootstrapped } from '@/lib/kiosk-token';
import type { KioskCurrentResponse } from '@/lib/types';
import { useRealtime } from '@/lib/useRealtime';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { CheckinForm } from './CheckinForm';

interface KioskInfo {
  accountId: string;
  facilityId: string;
  facilityName: string;
  kiosk: { id: string; name: string };
}

export function CheckinPage() {
  const [kioskInfo, setKioskInfo] = useState<KioskInfo | null>(null);
  const [current, setCurrent] = useState<KioskCurrentResponse | null>(null);
  const [submitted, setSubmitted] = useState(false);
  // Track connect failure so we can redirect to setup.
  const [connectFailed, setConnectFailed] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setCurrent(await kioskCurrent());
    } catch {
      setCurrent(null);
    }
  }, []);

  useEffect(() => {
    if (!isKioskBootstrapped()) return;
    kioskConnect()
      .then((info) => {
        setKioskInfo(info);
      })
      .catch(() => {
        setConnectFailed(true);
      });
  }, []);

  // Fetch current once kioskInfo resolves. Keyed on facilityId so a re-render
  // that keeps the same facility doesn't trigger a duplicate fetch.
  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh is stable (useCallback); kioskInfo object identity changes on every connect resolve, so we key on facilityId only
  useEffect(() => {
    if (!kioskInfo) return;
    void refresh();
  }, [kioskInfo?.facilityId]);

  // Realtime: listen for kiosk-intake-ready events and re-fetch current.
  // Store kioskInfo in a ref so the stable callback can read the latest value
  // without being listed as a dependency of useRealtime's internal effect.
  const kioskInfoRef = useRef<KioskInfo | null>(null);
  kioskInfoRef.current = kioskInfo;

  useRealtime(
    (event) => {
      if (event.type !== 'KIOSK_INTAKE_READY') return;
      const info = kioskInfoRef.current;
      if (!info) return;
      if (event.facility_id !== info.facilityId) return;
      void refresh();
    },
    { kiosk: true },
  );

  // Timer ref for the post-submit idle reset.
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleComplete = useCallback(() => {
    setSubmitted(true);
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      setSubmitted(false);
      void refresh();
    }, 5000);
  }, [refresh]);

  // Clean up timer on unmount.
  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  if (!isKioskBootstrapped() || connectFailed) {
    return <Navigate to="/checkin/setup" replace />;
  }

  if (!kioskInfo) {
    return <KioskWaiting message="Connecting…" />;
  }

  if (submitted || !current) {
    return (
      <KioskWaiting
        message={
          submitted ? 'Thank you. Have a great day.' : `Listening at ${kioskInfo.facilityName}…`
        }
      />
    );
  }

  return <CheckinForm current={current} onComplete={handleComplete} />;
}

function KioskWaiting({ message }: { message: string }) {
  return (
    <div className="grid min-h-[100dvh] place-items-center bg-background px-6 text-center">
      <div>
        <div
          className="mx-auto mb-6 size-3 animate-pulse rounded-full bg-primary"
          aria-hidden="true"
        />
        <p className="text-base text-muted-foreground">{message}</p>
      </div>
    </div>
  );
}
